import { ChangeDetectionStrategy, Component, OnInit, inject, signal, computed } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { InvitationValidationResponse } from '@zag/auth/models';
import { ToastService, FormField, Input, ZagButton, AppStatusComponent } from '@zag/ui';
import { AuthService } from '../auth.service';
import { InvitationAcceptanceService } from '../invitation-acceptance.service';
import { SocialLoginButtonsComponent } from '../social-login-buttons/social-login-buttons.component';

/**
 * Component for accepting email invitations.
 *
 * Handles four distinct cases driven by `InvitationValidationResponse`:
 *
 * 1. New account, email/password enabled (± social):
 *    Show name + password form. On submit: create account → auto sign-in → navigate to /.
 *
 * 2. New account, social-only (emailPassword = false):
 *    Show social provider buttons only. Clicking initiates the OAuth flow via
 *    AuthService.signInWithSocial(); the callbackURL loops back here so the
 *    invitation token can be consumed server-side after OAuth completes.
 *
 * 3. Existing account, email/password enabled (± social):
 *    Show password field to verify identity. On submit: accept → auto sign-in → navigate to /.
 *
 * 4. Existing account, social-only:
 *    Show social buttons only — same OAuth redirect, better-auth accepts on first login.
 *
 * Route: /auth/accept-invitation?token=xxx
 */
@Component({
  selector: 'sc-accept-invitation',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, FormField, Input, ZagButton, SocialLoginButtonsComponent, AppStatusComponent],
  template: `
    <div class="auth-container">
      <div class="auth-form">
        <h2 class="auth-header">
          <img src="/logo.svg" alt="Logo" />
          <span>{{ 'auth.acceptInvitation.title' | translate }}</span>
        </h2>

        <!-- Loading state -->
        @if (isLoadingValidation()) {
          <div class="loading-message">
            <p>{{ 'common.loading' | translate }}</p>
          </div>
        }

        <!-- Error state -->
        @if (validationError()) {
          <div class="error-message">
            <p>{{ validationError() | translate }}</p>
          </div>
        }

        <!-- Validation success -->
        @if (invitation() && !validationError() && !isLoadingValidation()) {
          <div class="invitation-info">
            <p>{{ 'auth.acceptInvitation.from' | translate: { name: $safeNavigationMigration(invitation()?.inviterName) } }}</p>
            @if (invitation()?.message) {
              <p class="invitation-message">{{ invitation()?.message }}</p>
            }
          </div>

          <!-- ── SOCIAL PROVIDERS ── -->
          @if (hasSocialProviders()) {
            <p class="section-label">
              @if (invitation()?.existingAccount) {
                {{ 'auth.acceptInvitation.loginWith' | translate }}
              } @else {
                {{ 'auth.acceptInvitation.signUpWith' | translate }}
              }
            </p>
            <sc-social-login-buttons [providers]="socialProviders()" [disabled]="isSubmitting()" (providerClick)="onSocialLogin($event)" />
          }

          <!-- Divider between social and email/password -->
          @if (hasSocialProviders() && hasEmailPassword()) {
            <div class="auth-divider">
              <span>{{ 'auth.or' | translate }}</span>
            </div>
          }

          <!-- ── NEW ACCOUNT — email/password form ── -->
          @if (!invitation()?.existingAccount && hasEmailPassword()) {
            <form [formGroup]="passwordForm" (ngSubmit)="onSubmitPassword()">
              <div class="form-fields">
                <zag-form-field
                  [label]="'auth.acceptInvitation.fullName' | translate"
                  [error]="passwordForm.get('name')?.touched && passwordForm.get('name')?.invalid ? ('common.required' | translate) : ''"
                  [required]="true"
                  forId="accept-name"
                >
                  <zag-input id="accept-name" formControlName="name" type="text"></zag-input>
                </zag-form-field>

                <zag-form-field
                  [label]="'auth.acceptInvitation.password' | translate"
                  [error]="
                    passwordForm.get('password')?.touched && passwordForm.get('password')?.hasError('required')
                      ? ('common.required' | translate)
                      : passwordForm.get('password')?.touched && passwordForm.get('password')?.hasError('minlength')
                        ? ('auth.passwordMinLength' | translate)
                        : ''
                  "
                  [required]="true"
                  forId="accept-password"
                >
                  <zag-input id="accept-password" formControlName="password" type="password"></zag-input>
                </zag-form-field>

                <button zagButton zagType="primary" type="submit" [disabled]="!passwordForm.valid || isSubmitting()" class="submit-button">
                  {{ isSubmitting() ? ('common.processing' | translate) : ('auth.acceptInvitation.createAccount' | translate) }}
                </button>
              </div>
            </form>
          }

          <!-- ── EXISTING ACCOUNT — password verification form ── -->
          @if (invitation()?.existingAccount && hasEmailPassword()) {
            <p class="existing-account-text">{{ 'auth.acceptInvitation.existingAccount' | translate }}</p>

            <form [formGroup]="existingForm" (ngSubmit)="onSubmitExisting()">
              <div class="form-fields">
                <zag-form-field [label]="'auth.email' | translate" forId="accept-email-existing">
                  <zag-input id="accept-email-existing" [value]="invitation()?.email ?? ''" [disabled]="true"></zag-input>
                </zag-form-field>

                <zag-form-field
                  [label]="'auth.acceptInvitation.orSignin' | translate"
                  [error]="
                    existingForm.get('password')?.touched && existingForm.get('password')?.invalid ? ('common.required' | translate) : ''
                  "
                  [required]="true"
                  forId="accept-password-existing"
                >
                  <zag-input id="accept-password-existing" formControlName="password" type="password"></zag-input>
                </zag-form-field>

                <button zagButton zagType="primary" type="submit" [disabled]="!existingForm.valid || isSubmitting()" class="submit-button">
                  {{ isSubmitting() ? ('common.processing' | translate) : ('auth.acceptInvitation.acceptAndLogin' | translate) }}
                </button>
              </div>
            </form>
          }
        }
      </div>
      <footer class="auth-status-footer">
        <zag-app-status />
      </footer>
    </div>
  `,
  styles: [
    `
      .auth-container {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 1rem;
        min-height: 100vh;
        min-height: 100svh;
        box-sizing: border-box;
      }

      .auth-status-footer {
        position: absolute;
        bottom: 1rem;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 100%;
        max-width: 400px;
      }

      .auth-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }

      .auth-header img {
        width: 92px;
        height: auto;
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .loading-message {
        padding: 0.75rem;
        text-align: center;
        color: var(--color-text-secondary, #6b7280);
        font-size: 0.875rem;
      }

      .error-message {
        padding: 0.75rem;
        background-color: var(--color-error-surface, #fee);
        border: 1px solid var(--color-error-border, #fcc);
        border-radius: 4px;
        color: var(--color-error, #c00);
        font-size: 0.875rem;
      }

      .invitation-info {
        padding: 0.75rem;
        background-color: var(--surface-color-default, #f9fafb);
        border-left: 3px solid var(--color-primary, #0066cc);
        border-radius: 4px;
        font-size: 0.875rem;
      }

      .invitation-message {
        margin-top: 0.5rem;
        font-style: italic;
        color: var(--color-text-secondary, #6b7280);
      }

      .section-label {
        font-size: 0.875rem;
        color: var(--color-text-secondary, #6b7280);
        margin: 0;
      }

      .existing-account-text {
        font-size: 0.875rem;
        color: var(--color-text-secondary, #6b7280);
        text-align: center;
      }

      .auth-divider {
        display: flex;
        align-items: center;
        gap: 1rem;
        color: var(--color-text-secondary, #6b7280);
        font-size: 0.875rem;
      }

      .auth-divider::before,
      .auth-divider::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: var(--color-border, #e5e7eb);
      }

      .submit-button {
        padding: 0.75rem 1.5rem;
        font-size: 1rem;
        font-weight: 500;
      }
    `,
  ],
})
export class AcceptInvitationComponent implements OnInit {
  private readonly invitationService = inject(InvitationAcceptanceService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly authService = inject(AuthService);

  private readonly token = signal<string | null>(null);
  protected readonly invitation = signal<InvitationValidationResponse | null>(null);
  protected readonly isLoadingValidation = signal(false);
  protected readonly validationError = signal<string | null>(null);
  protected readonly isSubmitting = signal(false);

  protected readonly hasEmailPassword = computed(() => this.invitation()?.availableLoginMethods.emailPassword ?? false);
  protected readonly hasSocialProviders = computed(() => (this.invitation()?.availableLoginMethods.socialProviders?.length ?? 0) > 0);

  /** Map raw provider ID strings into the shape SocialLoginButtonsComponent expects */
  protected readonly socialProviders = computed(() =>
    (this.invitation()?.availableLoginMethods.socialProviders ?? []).map(id => ({ providerId: id, displayName: id }))
  );

  protected readonly passwordForm = this.fb.group({
    name: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  protected readonly existingForm = this.fb.group({
    password: ['', [Validators.required]],
  });

  ngOnInit(): void {
    const token = this.route.snapshot.queryParams['token'];

    if (!token) {
      this.validationError.set('auth.acceptInvitation.invalidToken');
      return;
    }

    this.token.set(token);

    // If the user already has an active session (e.g. they just completed a social OAuth
    // flow that redirected back here), accept the invitation immediately without showing
    // the form again.
    this.authService.getSession().then(() => {
      if (this.authService.isAuthenticated()) {
        this.acceptWithSocialAndNavigate(token);
      } else {
        this.validateInvitation(token);
      }
    });
  }

  /**
   * Called when the user returns from an OAuth flow already authenticated.
   * Calls the server to assign roles and mark the invitation accepted, then navigates to /.
   */
  private acceptWithSocialAndNavigate(plainToken: string): void {
    this.isSubmitting.set(true);

    this.invitationService.accept({ token: plainToken, mode: 'social' }).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('auth.acceptInvitation.success'));
        this.router.navigate(['/']);
      },
      error: err => {
        console.error('Failed to accept invitation via social login:', err);
        // The invitation may already have been accepted or the emails don't match.
        // Fall back to showing the normal form so the user can see the error.
        this.isSubmitting.set(false);
        this.validateInvitation(plainToken);
      },
    });
  }

  private validateInvitation(plainToken: string): void {
    this.isLoadingValidation.set(true);
    this.validationError.set(null);

    this.invitationService.validate(plainToken).subscribe({
      next: response => {
        this.invitation.set(response);
        this.isLoadingValidation.set(false);

        // Pre-fill the name field if the invitation includes a suggested name
        if (response.inviteeName) {
          this.passwordForm.patchValue({ name: response.inviteeName });
        }
      },
      error: err => {
        console.error('Failed to validate invitation:', err);
        this.validationError.set('auth.acceptInvitation.validationFailed');
        this.isLoadingValidation.set(false);
      },
    });
  }

  protected onSubmitPassword(): void {
    if (!this.passwordForm.valid || !this.token()) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const email = this.invitation()?.email;
    const password = this.passwordForm.get('password')?.value;

    if (!email || !password) return;

    this.isSubmitting.set(true);

    const token = this.token();
    if (!token) return;

    this.invitationService
      .accept({
        token,
        mode: 'password',
        name: this.passwordForm.get('name')?.value ?? '',
        password,
      })
      .subscribe({
        next: () => this.signInAndNavigate(email, password),
        error: err => {
          console.error('Failed to accept invitation:', err);
          this.toast.error(this.translate.instant('auth.acceptInvitation.error'));
          this.isSubmitting.set(false);
        },
      });
  }

  protected onSubmitExisting(): void {
    if (!this.existingForm.valid || !this.token()) {
      this.existingForm.markAllAsTouched();
      return;
    }

    const email = this.invitation()?.email;
    const password = this.existingForm.get('password')?.value;

    if (!email || !password) return;

    this.isSubmitting.set(true);

    const token = this.token();
    if (!token) return;

    this.invitationService
      .accept({
        token,
        mode: 'existing',
        password,
      })
      .subscribe({
        next: () => this.signInAndNavigate(email, password),
        error: err => {
          console.error('Failed to accept invitation:', err);
          this.toast.error(this.translate.instant('auth.acceptInvitation.error'));
          this.isSubmitting.set(false);
        },
      });
  }

  /**
   * Sign in via better-auth to establish a session cookie, show success toast,
   * and navigate to the application root — completing the invitation flow with
   * no extra step required from the user.
   */
  private signInAndNavigate(email: string, password: string): void {
    this.invitationService.signInWithEmail({ email, password, rememberMe: false }).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('auth.acceptInvitation.success'));
        this.router.navigate(['/']);
      },
      error: err => {
        // Invitation was accepted but auto-login failed — redirect to login page
        // so the user can sign in manually. This should not normally happen.
        console.error('Auto-login after invitation acceptance failed:', err);
        this.toast.success(this.translate.instant('auth.acceptInvitation.success'));
        this.router.navigate(['/login']);
        this.isSubmitting.set(false);
      },
    });
  }

  /**
   * Initiate social login via better-auth. Uses the same POST-based flow as
   * LoginComponent (via AuthService.signInWithSocial) so the provider's
   * OAuth URL is returned in the response body rather than followed as a GET —
   * avoiding the 404 caused by the frontend router intercepting the /api/ path.
   *
   * The callbackURL loops back to this page so the invitation token can be
   * consumed server-side after OAuth completes.
   */
  protected onSocialLogin(providerId: string): void {
    const token = this.token();
    const callbackURL = new URL(token ? `/auth/accept-invitation?token=${encodeURIComponent(token)}` : '/', window.location.origin).href;

    this.authService.signInWithSocial(providerId, callbackURL);
  }
}
